import { useDeviceType } from "@dash/mq";
import { ProductionEnvAlert } from "@dash/nav";
import { SearchButton } from "@dash/search";
import { useTheme } from "@emotion/react";
import { GlobalSearchDialog } from "@megarax/home";
import { useContext, useEffect } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";

import { AccountHeader } from "../AccountHeader";
import { NavRecentDocuments } from "../history/NavRecentDocuments";
import { useRecordRecentlyViewed } from "../history/useRecordRecentlyViewed";
import { SignalsBellButton } from "../signals/SignalsBellButton";
import { WebPushContext } from "../WebPushContext";
import { MegaraxNavDrawer } from "./MegaraxNavDrawer";
import { desktopNavPadding, desktopNavScrollContentCss, desktopNavScrollCss, desktopNavScrollFooterCss, desktopNavWidth } from "./navConstants";
import { NavDivider } from "./NavDivider";
import { NavLinkTreeSections } from "./NavLinkTreeSections";
import { NavSignOutButton } from "./NavSignOutButton";
import { useMegaraxLinkTree } from "./useMegaraxLinkTree";
import { useNavAccentColor } from "./useNavAccentColor";

type Props = {
  showProductionEnvAlert?: boolean;
};

export const MegaraxNav: React.FC<Props> = ({ showProductionEnvAlert }) => {
  const { isDesktop } = useDeviceType();
  const theme = useTheme();
  const auth = useContext(IamAuthContext);
  const webPushContext = useContext(WebPushContext);
  const linkTree = useMegaraxLinkTree();
  const { accentColor, iconBackgroundColor } = useNavAccentColor(linkTree);

  useRecordRecentlyViewed();

  const unsubscribe = useClientManager("notifications").removeUserWebPushSub().useMutation();

  const onSignOut = () => {
    if (webPushContext?.endpoint) {
      unsubscribe.mutate({ endpoint: webPushContext.endpoint });
    }
    auth.signOut();
  };

  useEffect(() => {
    if (!isDesktop) {
      document.documentElement.style.removeProperty("--dash-nav-width");
      return;
    }

    document.documentElement.style.setProperty("--dash-nav-width", desktopNavWidth);

    return () => {
      document.documentElement.style.removeProperty("--dash-nav-width");
    };
  }, [isDesktop]);

  if (!isDesktop) {
    return (
      <MegaraxNavDrawer
        onSignOut={onSignOut}
        showProductionEnvAlert={showProductionEnvAlert}
        linkTree={linkTree}
      />
    );
  }

  return (
    <>
      <div
        css={{
          boxSizing: "border-box",
          width: desktopNavWidth,
          flexShrink: 0,
          position: "fixed",
          left: 0,
          top: 0,
          height: "100%",
          padding: desktopNavPadding,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "white",
          borderRight: `1px solid ${theme.colors.skeleton}`,
          zIndex: 999,
        }}
      >
        <div css={desktopNavScrollCss}>
          <div css={desktopNavScrollContentCss}>
            <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {auth.iamUser && (
                <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <div css={{ flex: 1, minWidth: 0 }}>
                    <AccountHeader
                      name={`${auth.iamUser.firstName} ${auth.iamUser.lastName}`}
                      groups={auth.iamUser.groups.map((group) => group.name)}
                      profilePictureUrl={auth.iamUser.profilePictureUrl}
                      hideSignOut
                      color="black"
                    />
                  </div>
                  <SignalsBellButton
                    variant="pale"
                    iconColor={accentColor}
                    dialogLeft={`calc(${desktopNavWidth} + 8px)`}
                  />
                </div>
              )}

              <GlobalSearchDialog
                renderTrigger={(onOpen) => (
                  <SearchButton
                    onClick={onOpen}
                    shortcut={{ key: "k", modifier: "ctrl" }}
                    variant="pale"
                    accentColor={accentColor}
                    accentBackgroundColor={iconBackgroundColor}
                  />
                )}
              />

              {showProductionEnvAlert && <ProductionEnvAlert />}
            </div>

            <NavDivider />

            <NavRecentDocuments />

            <NavDivider />

            <NavLinkTreeSections linkTree={linkTree} />

            <div css={desktopNavScrollFooterCss}>
              <NavDivider />
              <NavSignOutButton onSignOut={onSignOut} />
            </div>
          </div>
        </div>
      </div>
      <div css={{ boxSizing: "border-box", width: desktopNavWidth, flexShrink: 0 }} />
    </>
  );
};
